import { EnvironmentInjector, Injector, StaticProvider } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, take, tap } from 'rxjs/operators';

// handle destroying a manually created injector from Injector.create()
// see these links to understand what needs to be called
// https://github.com/angular/angular/blob/15.2.1/packages/core/src/di/create_injector.ts#L23-L48
// https://github.com/angular/angular/blob/15.2.1/packages/core/src/di/r3_injector.ts#L77-L132
// https://stackoverflow.com/a/77179156
export function destroyManualInjector(
  manualInjector?: Injector,
): void {
  if (manualInjector instanceof EnvironmentInjector) {
    manualInjector.destroy();
  }
}

// destroy the injector when the observable emits
// (like onDestroy$, or when modal closed)
export function destroyManualInjectorWhen<T>(
  manualInjector: Injector,
  onDestroyOrClosed$: Observable<T>,
): void {
  onDestroyOrClosed$.pipe(
    take(1),
    tap(() => {
      destroyManualInjector(manualInjector);
    }),
  ).subscribe();
}


// destroy the injector after the observable completes
// (ex: the injector is used for some async process, and we want to destroy it when it's done)
export function createScopedInjector$<T>(
  options: {
    providers: StaticProvider[];
    parent?: Injector;
    name?: string;
  },
  inputFn: (manualInjector: Injector) => Observable<T>,
): Observable<T> {
  const manualInjector = Injector.create(options);
  return inputFn(manualInjector).pipe(
    finalize(() => {
      destroyManualInjector(manualInjector);
    }),
  );
}


// destroy the injector after the Promise completes
// (ex: the injector is used for some async process, and we want to destroy it when it's done)
export async function createScopedInjectorP<T>(
  options: {
    providers: StaticProvider[];
    parent?: Injector;
    name?: string;
  },
  inputFn: (manualInjector: Injector) => Promise<T>,
): Promise<T> {
  const manualInjector = Injector.create(options);
  return inputFn(manualInjector).finally(() => {
    destroyManualInjector(manualInjector);
  });
}


// destroy the injector after the fn completes
// (ex: the injector is used for some process, and we want to destroy it when it's done)
export function createScopedInjector<T>(
  options: {
    providers: StaticProvider[];
    parent?: Injector;
    name?: string;
  },
  inputFn: (manualInjector: Injector) => T,
): T {
  const manualInjector = Injector.create(options);
  try {
    return inputFn(manualInjector);
  } finally {
    destroyManualInjector(manualInjector);
  }
}
